import type { GuitarChart } from '@shared/guitarChart';
import { GUITAR_ENGINE_TUNING, GUITAR_STRUM_BAR_Y_FRAC } from '@shared/guitarChart';
import { MANIA_LANE_COUNT, type ManiaLane } from '@shared/maniaNote';
import type {
  ManiaReplayInput,
  ManiaReplayInputs,
  ReplayOutcome,
} from '@shared/replay';
import {
  buildManiaEngineConfig,
  step,
  summariseManiaAccuracy,
  type ManiaEngineEvent,
  type ManiaEngineInput,
  type ManiaHpChangedPayload,
} from '@shared/engine/mania/core';
import {
  createManiaInitialState,
  resetManiaEngineStateForNextLoop,
  type ManiaEngineConfig,
  type ManiaEngineState,
  type ManiaLaneEvent,
  type ManiaResolution,
} from '@shared/engine/mania/state';
import {
  milestoneIsStar,
  milestoneTier,
  type MilestoneKind,
} from '@shared/progression';
import { AudioEngine, clampPlaybackRate } from '../audio/scheduler';
import { loadVolumePrefs } from '../audio/volumePrefs';
import { LoopRunState } from '../loopManager';
import { RunTimeline } from '../input/runTimeline';
import type { RunTimingReport } from '@shared/antiCheat/runTiming';
import { getRenderQuality } from '../rendering/renderQuality';
import {
  KeyboardGuitarInputSource,
  ReplayGuitarInputSource,
  type GuitarInputSource,
} from './guitarInputSource';
import { GuitarRenderer } from './guitarRenderer';
import { DEFAULT_KEY_LABELS } from './render/guitarRenderConstants';
import { describeKeyCode, loadKeybindPrefs } from '../keybindPrefs';
import type {
  GuitarFloatingText,
  GuitarLaneFlashSpec,
  GuitarParticleEmission,
  GuitarScorePop,
  GuitarShakeSpec,
  GuitarShockwaveSpec,
} from './render/guitarRenderTypes';

export type GuitarSessionStatus =
  | 'idle'
  | 'starting'
  | 'lead_in'
  | 'running'
  | 'paused'
  | 'awaiting_next_loop'
  | 'completed'
  | 'failed';

export interface GuitarMilestoneInfo {
  kind: MilestoneKind;
  loopIndex: number;
  starsEarned: number;
  cupsEarned: number;
  pause: boolean;
}

export interface GuitarGameSessionOptions {
  chart: GuitarChart;
  canvas: HTMLCanvasElement;
  audioOffsetMs: number;
  /** Pre-recorded inputs (for replay verifiers / debug). */
  replayInputs?: ManiaEngineInput[];
  onStatusChange?: (status: GuitarSessionStatus) => void;
  onComplete?: (outcome: ReplayOutcome) => void;
  onEvent?: (event: ManiaEngineEvent) => void;
  onMilestone?: (info: GuitarMilestoneInfo) => void;
  testHook?: boolean;
  enableLoops?: boolean;
}

const LEAD_IN_BEATS = 4;
/** See `MIN_LEAD_IN_WALL_SECONDS` in circlesSession.ts — same logic. */
const MIN_LEAD_IN_WALL_SECONDS = 2.0;
/** Score-roll-up easing. See SCORE_DISPLAY_LERP_RATE in circlesSession.ts. */
const SCORE_DISPLAY_LERP_RATE = 15;
/** Floating-text lifetime in seconds for verdict pops. */
const FLOATING_TEXT_LIFE = 0.55;
/** Longer lifetime for emphasised miss labels so they read clearly. */
const FLOATING_TEXT_LIFE_MISS = 0.72;
/** Bigger / longer lifetime for centre-screen combo milestone banners. */
const BIG_FLOATING_TEXT_LIFE = 1.1;
/** Score-pop flyer lifetime. */
const SCORE_POP_LIFE = 0.75;
/** Combos at which to fire a celebration banner + fanfare. */
const COMBO_MILESTONES = [10, 25, 50, 100] as const;
/** Combo at which a single miss becomes a "big combo break" — bigger shake + deeper duck. */
const BIG_COMBO_BREAK_THRESHOLD = 25;
/**
 * Cadence (seconds) at which a sustained hold emits an ambient
 * "you're scoring" sparkle. Same cadence Piano uses — dense enough
 * to feel continuous, sparse enough that long holds don't snow the
 * playfield.
 */
const HELD_SPARKLE_INTERVAL_SEC = 0.11;
/**
 * Hit-stop micro-freeze tunings (ms). Symmetric with Piano so muscle
 * memory transfers between modes.
 */
const HIT_STOP_MS_PERFECT = 50;
const HIT_STOP_MS_PERFECT_BIG_COMBO = 70;
const HIT_STOP_MS_GOOD = 20;
/** Music duck-pulse tunings on miss + on big-combo break. */
const MUSIC_DUCK_MISS = { dip: 0.72, attackMs: 25, releaseMs: 90 };
const MUSIC_DUCK_COMBO_BREAK = { dip: 0.5, attackMs: 40, releaseMs: 280 };
/**
 * Subtle sidechain duck on every perfect — the music dips ~1 dB so
 * the hit click pops through the mix without the player consciously
 * registering the dip. `AudioEngine.duckMusicPulse` cancels pending
 * ramps and re-attacks from the current value, so dense perfect
 * streams keep the music gently pumping rather than swelling all
 * the way back between hits.
 */
const MUSIC_DUCK_PERFECT_SIDECHAIN = { dip: 0.9, attackMs: 6, releaseMs: 90 };
/**
 * Minimum spacing (ms) between sidechain duck pulses. Without this,
 * jack streams at 16th-note spacing hit the same audio param at
 * frame rate, producing audible 60 Hz warble instead of musical
 * pumping. 35 ms ≈ 28 Hz max pump rate — below the audible warble
 * floor and above typical perfect-streak density.
 */
const SIDECHAIN_DUCK_MIN_INTERVAL_MS = 35;
/**
 * "Crash" duck used when the player misses ≥
 * {@link MULTI_MISS_THRESHOLD} times within
 * {@link MULTI_MISS_WINDOW_MS}. Heavier than the regular
 * combo-break duck so the player viscerally feels they're crashing
 * out, not just dropping a single note.
 */
const MUSIC_DUCK_CRASH = { dip: 0.32, attackMs: 50, releaseMs: 420 };
/**
 * Music lowpass cutoff sweep applied alongside the crash duck. Dips
 * to ~520 Hz so the music loses its top end as well as its volume —
 * the "amp blown out" reading the duck alone can't deliver. Returns
 * to neutral over ~360 ms so the lowpass tail doesn't drag past the
 * duck release.
 */
const MUSIC_LOWPASS_CRASH = { toFreq: 520, attackMs: 55, sustainMs: 130, releaseMs: 360 };
/** Multi-miss "crash" detection: count + time window. */
const MULTI_MISS_THRESHOLD = 3;
const MULTI_MISS_WINDOW_MS = 700;
/**
 * Verdict colours for verdict-text pops. Slightly warmer than Piano's
 * cyan / mint vocabulary so the floating "PERFECT" / "Good" / "Miss"
 * labels read on Guitar's distinct fretboard backdrop.
 */
const VERDICT_COLOURS = {
  perfect: '#3bff9d',
  good: '#16e0ff',
  miss: '#ff3b5c',
} as const;
const MILESTONE_COLOUR = '#ffd84d';
const LIFELINE_COLOUR = '#3bd8ff';
const DEATH_COLOUR = '#ff3b5c';

function effectiveLeadInBeats(baseBeats: number, bpm: number, startSpeed: number): number {
  const effectiveSpeed = clampPlaybackRate(startSpeed);
  const wallTime = (baseBeats * 60) / bpm / effectiveSpeed;
  if (wallTime >= MIN_LEAD_IN_WALL_SECONDS) {
    return baseBeats;
  }
  return Math.ceil((MIN_LEAD_IN_WALL_SECONDS * bpm * effectiveSpeed) / 60);
}

/**
 * Read the current per-lane key labels from keybind prefs, falling
 * back to the defaults (D / F / J / K) when a slot is empty. Used by
 * the renderer to paint the labels under the strum-bar fret caps so
 * the displayed letter always matches what the player has bound.
 */
function loadGuitarKeyLabels(): Record<ManiaLane, string> {
  const prefs = loadKeybindPrefs();
  const out = { ...DEFAULT_KEY_LABELS } as Record<ManiaLane, string>;
  for (let lane = 0; lane < MANIA_LANE_COUNT; lane += 1) {
    const lanePrefs = prefs.guitarLanes[lane];
    const primary = lanePrefs[0];
    if (primary) {
      out[lane as ManiaLane] = describeKeyCode(primary);
    }
  }
  return out;
}

/**
 * Guitar-mode game session. Mirrors {@link
 * import('../piano/pianoSession.js').PianoGameSession}'s lifecycle
 * + visual-feedback pipeline so the play-page code parallels cleanly
 * between modes:
 *
 *   constructor → start() → tick loop → pause/resume → destroy
 *
 * Reuses the same {@link AudioEngine}, {@link LoopRunState}, and the
 * universal star/cup milestone vocabulary. Mode-specific bits are
 * the engine tuning ({@link GUITAR_ENGINE_TUNING}), the renderer
 * (perspective fretboard), and the keyboard / pointer-per-lane
 * input source (Guitar's `D F J K` defaults + perspective lane
 * geometry).
 */
export class GuitarGameSession {
  private chart: GuitarChart;
  private engineConfig: ManiaEngineConfig;
  private state: ManiaEngineState;
  private audio: AudioEngine;
  private renderer: GuitarRenderer;
  private inputSource: GuitarInputSource;
  /** Anti-cheat timing recorder (real-time progression + isTrusted). */
  private timeline: RunTimeline;
  private floatingTexts: GuitarFloatingText[] = [];
  private scorePops: GuitarScorePop[] = [];
  private shake: GuitarShakeSpec = { amount: 0, decay: 0 };
  private particleEmissionsThisFrame: GuitarParticleEmission[] = [];
  private shockwaveSpecsThisFrame: GuitarShockwaveSpec[] = [];
  private laneFlashSpecsThisFrame: GuitarLaneFlashSpec[] = [];
  private rafId: number | null = null;
  private lastBeat = 0;
  private status: GuitarSessionStatus = 'idle';
  private opts: GuitarGameSessionOptions;
  private outcome: ReplayOutcome | null = null;
  private allEvents: ManiaEngineEvent[] = [];
  private loopCapturedInputs: ManiaReplayInput[][] = [[]];
  private loopState: LoopRunState;
  private enableLoops: boolean;
  private destroyed = false;
  private autoPaused = false;
  private lastTickAt = performance.now();
  private displayedScore = 0;
  private comboMilestonesHit = new Set<number>();
  private lastPerfectShakeAt = 0;
  private heldSparkleTimers: Map<ManiaLane, number> = new Map();
  private reducedMotion = false;
  private cachedRect: { width: number; height: number } = { width: 0, height: 0 };
  private resizeObserver: ResizeObserver | null = null;
  private keyLabels: Record<ManiaLane, string>;
  /**
   * Lane-event id → event lookup. Lets {@link handleEvent} ask "was
   * this resolution the dual half of a chord?" in O(1) so the audio
   * layer can fire the dedicated chord-stab on dual-half resolutions
   * only (avoids double-firing once for primary and once for dual on
   * every chord).
   */
  private laneEventsById: Map<string, ManiaLaneEvent>;
  /** Wall-clock timestamp of the most recent sidechain duck. */
  private lastSidechainDuckAt = 0;
  /**
   * Rolling miss timestamps (performance.now). Trimmed to the most
   * recent {@link MULTI_MISS_WINDOW_MS} on every push so the length
   * is the "misses within the window" count.
   */
  private recentMissTimestamps: number[] = [];
  /**
   * Latches true the first time a multi-miss crash fires inside an
   * active window, so a 5-miss burst doesn't trigger the heavy crash
   * boom + lowpass sweep + crash-duck three times in a row. Cleared
   * automatically once the rolling window empties (the player has
   * stabilised) — see {@link registerMissAndDetectCrash}.
   */
  private crashLatched = false;
  /**
   * Lanes with a wammy hold-drone currently sustaining. Reconciled
   * every tick by {@link maintainHoldDrones} against
   * {@link ManiaEngineState.heldLanes} so a hold start fades a drone
   * in and a hold end fades it out. Skipped on low render-quality
   * devices ({@link audioPolishEnabled}).
   */
  private activeHoldDroneLanes: Set<ManiaLane> = new Set();
  /**
   * Whether the device gets the heavier audio polish path. Mirrors
   * the render-quality tier — touch-class / low-core hardware gets
   * the lighter audio loop (no hold drones) so we don't tax the
   * audio context's voice budget on phones.
   */
  private audioPolishEnabled: boolean;

  constructor(opts: GuitarGameSessionOptions) {
    this.opts = opts;
    this.chart = opts.chart;
    this.enableLoops = opts.enableLoops ?? false;
    this.loopState = new LoopRunState(this.chart.totalBeats);
    this.engineConfig = buildManiaEngineConfig(this.chart, GUITAR_ENGINE_TUNING);
    this.state = createManiaInitialState(this.chart);
    this.audio = new AudioEngine({ audioOffsetMs: opts.audioOffsetMs });
    // Splice the compressor + soft saturator + reverb send into the
    // SFX path so every layered Guitar hit (chord stab, power chord,
    // ring-out, slapback) sits glued in a venue ambience instead of
    // stacking dry transients. Guitar-only — other modes use the same
    // `AudioEngine` class with this off.
    this.audio.enableStageBus();
    this.audioPolishEnabled = getRenderQuality().tier === 'high';
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    const vols = loadVolumePrefs();
    this.audio.setMasterVolume(vols.master);
    this.audio.setMusicVolume(vols.music);
    this.audio.setSfxVolume(vols.sfx);
    this.renderer = new GuitarRenderer(opts.canvas);
    this.attachResizeObserver();
    this.keyLabels = loadGuitarKeyLabels();
    this.laneEventsById = new Map();
    for (const ev of this.engineConfig.sortedLaneEvents) {
      this.laneEventsById.set(ev.id, ev);
    }

    if (opts.replayInputs) {
      this.inputSource = new ReplayGuitarInputSource(opts.replayInputs);
    } else {
      this.inputSource = new KeyboardGuitarInputSource(
        opts.canvas,
        () => this.audio.getCurrentBeat(),
        () => this.status === 'lead_in' || this.status === 'running',
      );
    }

    this.timeline = new RunTimeline({
      getBeatAtNow: () => this.audio.getCurrentBeat(),
      isClockRunning: () => this.status === 'running',
    });
    this.timeline.attach();

    if (opts.testHook) {
      (window as unknown as {
        __beatHeavenGuitarTestHook?: GuitarGameSessionTestHook;
      }).__beatHeavenGuitarTestHook = this.buildTestHook();
    }

    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  private onVisibilityChange = (): void => {
    if (document.hidden) {
      if (this.status === 'running' || this.status === 'lead_in') {
        this.autoPaused = true;
        this.pause();
      }
    } else if (this.autoPaused && this.status === 'paused') {
      this.autoPaused = false;
      this.resume().catch((err) => {
        console.warn('Auto-resume after tab return failed:', err);
      });
    }
  };

  async start(): Promise<void> {
    if (this.destroyed) {
      return;
    }
    this.setStatus('starting');
    const ramp = this.loopState.getCurrentSpeedRamp();
    const leadIn = effectiveLeadInBeats(LEAD_IN_BEATS, this.chart.bpm, ramp.startSpeed);
    await this.audio.start(this.chart, leadIn, { speedRamp: ramp });
    if (this.destroyed) {
      return;
    }
    this.audio.scheduleLeadInTicks(leadIn);
    this.setStatus('lead_in');
    if (document.hidden) {
      this.autoPaused = true;
      this.pause();
      return;
    }
    this.tick();
  }

  async startNextLoop(): Promise<void> {
    if (this.destroyed) {
      return;
    }
    if (this.status !== 'awaiting_next_loop') {
      return;
    }
    this.loopState.advanceLoop();
    resetManiaEngineStateForNextLoop(this.state);
    this.engineConfig = buildManiaEngineConfig(this.chart, GUITAR_ENGINE_TUNING);
    this.laneEventsById.clear();
    for (const ev of this.engineConfig.sortedLaneEvents) {
      this.laneEventsById.set(ev.id, ev);
    }
    this.lastBeat = 0;
    this.loopCapturedInputs.push([]);
    this.timeline.startLoop(this.loopCapturedInputs.length - 1);
    this.comboMilestonesHit.clear();
    this.floatingTexts.length = 0;
    this.scorePops.length = 0;
    this.shake.amount = 0;
    this.shake.decay = 0;
    this.lastPerfectShakeAt = 0;
    this.heldSparkleTimers.clear();
    this.recentMissTimestamps.length = 0;
    this.crashLatched = false;
    this.lastSidechainDuckAt = 0;
    this.activeHoldDroneLanes.clear();
    this.audio.clearGuitarAudioFx();
    this.renderer.clearLoopBoundFx();

    this.setStatus('starting');
    const ramp = this.loopState.getCurrentSpeedRamp();
    const leadIn = effectiveLeadInBeats(LEAD_IN_BEATS, this.chart.bpm, ramp.startSpeed);
    await this.audio.restartForLoop(this.chart, leadIn, ramp);
    if (this.destroyed) {
      return;
    }
    this.audio.scheduleLeadInTicks(leadIn);
    this.setStatus('lead_in');
    if (document.hidden) {
      this.autoPaused = true;
      this.pause();
      return;
    }
    this.lastTickAt = performance.now();
    this.tick();
  }

  getCurrentBeat(): number {
    return this.audio.getCurrentBeat();
  }

  pause(): void {
    if (this.status === 'running' || this.status === 'lead_in') {
      this.setStatus('paused');
      if (this.rafId !== null) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      void this.audio.suspend().catch((err) => {
        console.warn('Failed to suspend audio on pause:', err);
      });
    }
  }

  manualPause(): void {
    this.autoPaused = false;
    this.pause();
  }

  async resume(): Promise<void> {
    if (this.status === 'paused') {
      this.autoPaused = false;
      await this.audio.resume();
      this.setStatus('running');
      this.tick();
    }
  }

  async destroy(): Promise<void> {
    this.destroyed = true;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.inputSource.destroy();
    this.timeline.detach();
    await this.audio.stop();
    delete (window as unknown as {
      __beatHeavenGuitarTestHook?: GuitarGameSessionTestHook;
    }).__beatHeavenGuitarTestHook;
  }

  getOutcome(): ReplayOutcome | null {
    return this.outcome;
  }

  getStatus(): GuitarSessionStatus {
    return this.status;
  }

  getCapturedReplay(): ManiaReplayInputs {
    return {
      loops: this.loopCapturedInputs.map((inputs) => ({ inputs: inputs.slice() })),
      audioOffsetMs: this.opts.audioOffsetMs,
    };
  }

  /** Anti-cheat timing stream for this run (null if nothing captured). */
  getTimingReport(): RunTimingReport | null {
    return this.timeline.toReport();
  }

  getProgressionEarnedThisRun(): { stars: number; cups: number } {
    return {
      stars: this.loopState.countStars(),
      cups: this.loopState.countCups(),
    };
  }

  getLoopIndex(): number {
    return this.loopState.getLoopIndex();
  }

  getLoopsCleared(): number {
    return this.loopState.getLoopIndex();
  }

  applyVolumePrefs(): void {
    const vols = loadVolumePrefs();
    this.audio.setMasterVolume(vols.master);
    this.audio.setMusicVolume(vols.music);
    this.audio.setSfxVolume(vols.sfx);
  }

  private setStatus(s: GuitarSessionStatus): void {
    if (this.status === s) {
      return;
    }
    this.status = s;
    this.opts.onStatusChange?.(s);
  }

  private tick = (): void => {
    if (this.status === 'paused') {
      return;
    }

    const currentBeat = this.audio.getCurrentBeat();
    const now = performance.now();
    const dt = Math.min(0.05, Math.max(0, (now - this.lastTickAt) / 1000));
    this.lastTickAt = now;

    if (currentBeat < 0) {
      const leadInBeat = this.computeLeadInBeat();
      this.drawFrame(currentBeat, leadInBeat, dt);
      this.rafId = requestAnimationFrame(this.tick);
      return;
    }

    if (this.status === 'lead_in') {
      this.setStatus('running');
    }

    const cappedTarget = Math.min(currentBeat, this.lastBeat + this.audio.maxAdvanceBeatsPerFrame());

    const totalBeats = this.chart.totalBeats;
    const rawInputs = this.inputSource.poll(cappedTarget);
    // Clamp each input into `[this.lastBeat, cappedTarget]` (also capped
    // at `totalBeats`) rather than dropping out-of-range strums:
    //   - Lead-in strums arrive stamped with a NEGATIVE beat (the audio
    //     clock is still pre-roll). Clamping them up to `this.lastBeat`
    //     (beat 0 on the opening frame) lets the opening gems be hit by a
    //     strum thrown in the final moments of the countdown instead of
    //     silently eating the press. (The engine exempts these beat-0
    //     strums from the empty-tap penalty — see `handleTap`.)
    //   - Late strums the player squeezes in after the song ends get
    //     pulled back to `totalBeats`; the headless verifier clamps its
    //     tick window the same way.
    // Every captured beat therefore stays in `[0, totalBeats]`, so the
    // replay verifier (which steps from beat 0) re-simulates identically.
    const clampedInputs: ManiaEngineInput[] = rawInputs.map((inp) => ({
      beat: Math.max(
        this.lastBeat,
        Math.min(inp.beat, cappedTarget, totalBeats),
      ),
      lane: inp.lane,
      type: inp.type,
      source: inp.source,
    }));
    const activeLoopCapture = this.loopCapturedInputs[this.loopCapturedInputs.length - 1];
    for (const inp of clampedInputs) {
      activeLoopCapture.push({
        beat: inp.beat,
        lane: inp.lane,
        type: inp.type,
        source: inp.source,
      });
    }

    const out = step({
      config: this.engineConfig,
      state: this.state,
      fromBeat: this.lastBeat,
      toBeat: cappedTarget,
      inputs: clampedInputs,
    });
    this.lastBeat = Math.max(this.lastBeat, cappedTarget);
    for (const ev of out.events) {
      this.handleEvent(ev);
      this.allEvents.push(ev);
      this.opts.onEvent?.(ev);
    }

    this.detectAndFireMidRunMilestones();
    this.tickDisplayedScore(dt);
    this.advanceFloatingTexts(dt);
    this.advanceScorePops(dt);
    this.advanceShake(dt);
    this.maintainHeldSparkles(dt);
    this.maintainHoldDrones();

    this.drawFrame(this.state.beat, null, dt);

    // Retain the array references and just truncate so we avoid 3 ×
    // array allocations per frame. The renderer's PianoFxSystem
    // dedupes spec objects via WeakSet, and the spec objects pushed
    // each frame are fresh literals — so reusing the containers is
    // safe and saves ~180 array allocations/sec at 60fps.
    this.particleEmissionsThisFrame.length = 0;
    this.shockwaveSpecsThisFrame.length = 0;
    this.laneFlashSpecsThisFrame.length = 0;

    if (!this.state.alive && this.status !== 'failed') {
      this.finishSession('failed');
      return;
    }
    if (this.state.completed) {
      this.handleLoopComplete();
      return;
    }

    this.rafId = requestAnimationFrame(this.tick);
  };

  private detectAndFireMidRunMilestones(): void {
    if (!this.enableLoops) {
      return;
    }
    const candidates = this.loopState.detectMidRunMilestones(this.state.beat);
    for (const kind of candidates) {
      if (this.loopState.markFired(kind)) {
        this.audio.playGuitarMilestoneFanfare(milestoneTier(kind));
        this.spawnMilestoneFx(kind);
        this.opts.onMilestone?.({
          kind,
          loopIndex: this.loopState.getLoopIndex(),
          starsEarned: this.loopState.countStars(),
          cupsEarned: this.loopState.countCups(),
          pause: false,
        });
      }
    }
  }

  private tickDisplayedScore(dt: number): void {
    const target = this.state.score;
    if (this.displayedScore < target) {
      const k = 1 - Math.exp(-SCORE_DISPLAY_LERP_RATE * dt);
      this.displayedScore += (target - this.displayedScore) * k;
      if (target - this.displayedScore < 0.5) {
        this.displayedScore = target;
      }
    } else if (this.displayedScore > target) {
      this.displayedScore = target;
    }
  }

  private advanceFloatingTexts(dt: number): void {
    if (this.floatingTexts.length === 0) {
      return;
    }
    const survivors: GuitarFloatingText[] = [];
    for (const t of this.floatingTexts) {
      if (!t.big) {
        t.y -= 50 * dt;
      }
      t.life -= dt;
      if (t.life > 0) {
        survivors.push(t);
      }
    }
    this.floatingTexts = survivors;
  }

  private advanceScorePops(dt: number): void {
    if (this.scorePops.length === 0) {
      return;
    }
    const survivors: GuitarScorePop[] = [];
    for (const p of this.scorePops) {
      p.life -= dt;
      if (p.life > 0) {
        survivors.push(p);
      }
    }
    this.scorePops = survivors;
  }

  private advanceShake(dt: number): void {
    if (this.shake.amount > 0) {
      this.shake.amount = Math.max(0, this.shake.amount - this.shake.decay * dt);
    }
  }

  /**
   * Reconcile the wammy hold-drone voices against
   * {@link ManiaEngineState.heldLanes}: any newly-held lane gets a
   * drone faded in; any drone whose lane has been released
   * (manually, expired, or via auto-release) gets faded out. Skipped
   * on low-tier devices to keep the audio voice budget headroom for
   * the click stack.
   */
  private maintainHoldDrones(): void {
    if (!this.audioPolishEnabled) {
      return;
    }
    const held = this.state.heldLanes;
    if (held.size === 0 && this.activeHoldDroneLanes.size === 0) {
      return;
    }
    for (const lane of held.keys()) {
      if (!this.activeHoldDroneLanes.has(lane)) {
        const pan = (lane / (MANIA_LANE_COUNT - 1)) * 1.4 - 0.7;
        this.audio.startGuitarHoldDrone(lane, { pan });
        this.activeHoldDroneLanes.add(lane);
      }
    }
    if (this.activeHoldDroneLanes.size > 0) {
      for (const lane of this.activeHoldDroneLanes) {
        if (!held.has(lane)) {
          this.audio.stopGuitarHoldDrone(lane);
          this.activeHoldDroneLanes.delete(lane);
        }
      }
    }
  }

  private maintainHeldSparkles(dt: number): void {
    if (this.reducedMotion || dt <= 0) {
      return;
    }
    const held = this.state.heldLanes;
    if (held.size === 0) {
      if (this.heldSparkleTimers.size > 0) {
        this.heldSparkleTimers.clear();
      }
      return;
    }
    for (const lane of this.heldSparkleTimers.keys()) {
      if (!held.has(lane)) {
        this.heldSparkleTimers.delete(lane);
      }
    }
    for (const lane of held.keys()) {
      const prev = this.heldSparkleTimers.get(lane) ?? 0;
      let next = prev + dt;
      while (next >= HELD_SPARKLE_INTERVAL_SEC) {
        next -= HELD_SPARKLE_INTERVAL_SEC;
        const { x, y } = this.laneHitPoint(lane);
        this.particleEmissionsThisFrame.push({
          type: 'hold_sustain',
          x,
          y,
          colour: this.chart.palette.padPerfect,
        });
      }
      this.heldSparkleTimers.set(lane, next);
    }
  }

  private handleLoopComplete(): void {
    const completionMilestone = this.enableLoops
      ? this.loopState.detectCompletionMilestone()
      : null;
    if (completionMilestone && this.loopState.markFired(completionMilestone)) {
      this.audio.playGuitarMilestoneFanfare(milestoneTier(completionMilestone));
      this.spawnMilestoneFx(completionMilestone);
      const isLoopOneFinale =
        this.loopState.getLoopIndex() === 0 && milestoneIsStar(completionMilestone);
      this.opts.onMilestone?.({
        kind: completionMilestone,
        loopIndex: this.loopState.getLoopIndex(),
        starsEarned: this.loopState.countStars(),
        cupsEarned: this.loopState.countCups(),
        pause: isLoopOneFinale,
      });
    }
    if (this.enableLoops) {
      this.setStatus('awaiting_next_loop');
      if (this.rafId !== null) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      const isLoopOneFinale = this.loopState.getLoopIndex() === 0;
      if (isLoopOneFinale) {
        return;
      }
      void this.startNextLoop();
      return;
    }
    if (this.status !== 'completed') {
      this.finishSession('completed');
    }
  }

  private computeLeadInBeat(): number | null {
    const songTime = this.audio.getSongTime();
    if (!isFinite(songTime)) {
      return null;
    }
    if (songTime >= 0) {
      return null;
    }
    const beatsRemaining = -songTime * (this.chart.bpm / 60);
    return Math.max(0, beatsRemaining);
  }

  /**
   * Pixel coordinates of the visual centre of a lane at the strum
   * bar. Delegates to {@link GuitarRenderer.getLaneHitPoint} so
   * lane geometry stays single-source.
   */
  private laneHitPoint(lane: number): { x: number; y: number } {
    return this.renderer.getLaneHitPoint(
      lane as 0 | 1 | 2 | 3,
      this.cachedRect.width,
      this.cachedRect.height,
    );
  }

  private attachResizeObserver(): void {
    const rect = this.opts.canvas.getBoundingClientRect();
    this.cachedRect.width = rect.width;
    this.cachedRect.height = rect.height;
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    this.resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      this.cachedRect.width = entry.contentRect.width;
      this.cachedRect.height = entry.contentRect.height;
    });
    this.resizeObserver.observe(this.opts.canvas);
  }

  private drawFrame(beat: number, leadInBeat: number | null, _dt: number): void {
    this.renderer.draw({
      chart: this.chart,
      state: this.state,
      laneEvents: this.engineConfig.sortedLaneEvents as ReadonlyArray<ManiaLaneEvent>,
      beat,
      width: this.cachedRect.width,
      height: this.cachedRect.height,
      pressedLanes: this.inputSource.getPressedLanes(),
      displayedScore: this.displayedScore,
      countdown: leadInBeat,
      floatingTexts: this.floatingTexts,
      particleEmissions: this.particleEmissionsThisFrame,
      shockwaveSpecs: this.shockwaveSpecsThisFrame,
      laneFlashSpecs: this.laneFlashSpecsThisFrame,
      scorePops: this.scorePops,
      shake: this.shake,
      paused: this.status === 'paused',
      perfectOverlapBeats: this.engineConfig.perfectOverlapBeats,
      autoMissCutoffBeats:
        this.engineConfig.perfectOverlapBeats + this.engineConfig.autoMissLateBeats,
      keyLabels: this.keyLabels,
    });
  }

  private spawnVerdictText(res: ManiaResolution): void {
    const guitarVerdict: 'perfect' | 'good' | 'miss' =
      res.verdict === 'perfect' ? 'perfect' : res.verdict === 'miss' ? 'miss' : 'good';
    let label: string;
    if (res.kind === 'release') {
      label = guitarVerdict === 'miss' ? 'BROKEN' : 'RELEASE';
    } else {
      label = guitarVerdict === 'perfect' ? 'PERFECT' : guitarVerdict === 'good' ? 'Good' : 'Miss';
    }
    const colour = VERDICT_COLOURS[guitarVerdict];
    const strumBarY = this.cachedRect.height * GUITAR_STRUM_BAR_Y_FRAC;
    const isMiss = guitarVerdict === 'miss';
    const life = isMiss ? FLOATING_TEXT_LIFE_MISS : FLOATING_TEXT_LIFE;
    this.floatingTexts.push({
      text: label,
      lane: res.lane,
      y: strumBarY - 30,
      colour,
      life,
      maxLife: life,
      emphasised: isMiss,
    });
  }

  private spawnBigBanner(text: string, colour: string): void {
    this.floatingTexts.push({
      text,
      lane: 0,
      y: 0,
      colour,
      life: BIG_FLOATING_TEXT_LIFE,
      maxLife: BIG_FLOATING_TEXT_LIFE,
      big: true,
    });
  }

  private spawnScorePop(
    lane: number,
    verdict: 'perfect' | 'good',
    scoreDelta: number,
    newCombo: number,
  ): void {
    const { x, y } = this.laneHitPoint(lane);
    const target = this.renderer.getScoreHudTarget(
      this.cachedRect.width,
      this.cachedRect.height,
    );
    const colour = verdict === 'perfect' ? VERDICT_COLOURS.perfect : VERDICT_COLOURS.good;
    const big = verdict === 'perfect' && newCombo >= BIG_COMBO_BREAK_THRESHOLD;
    this.scorePops.push({
      text: `+${Math.round(scoreDelta)}`,
      startX: x,
      startY: y - 40,
      targetX: target.x,
      targetY: target.y,
      colour,
      big,
      life: SCORE_POP_LIFE,
      maxLife: SCORE_POP_LIFE,
    });
  }

  private spawnHitFx(
    lane: number,
    verdict: 'perfect' | 'good' | 'miss',
    newCombo: number,
    kind: ManiaResolution['kind'],
  ): void {
    const { x, y } = this.laneHitPoint(lane);
    const colour =
      verdict === 'perfect'
        ? VERDICT_COLOURS.perfect
        : verdict === 'good'
          ? VERDICT_COLOURS.good
          : VERDICT_COLOURS.miss;
    const isSuccessfulRelease = kind === 'release' && verdict !== 'miss';

    this.particleEmissionsThisFrame.push({
      type:
        verdict === 'perfect'
          ? 'hit_perfect'
          : verdict === 'good'
            ? 'hit_good'
            : 'hit_miss',
      x,
      y,
      colour,
    });

    if (isSuccessfulRelease) {
      this.particleEmissionsThisFrame.push({
        type: 'hold_release',
        x,
        y,
        colour,
      });
    }

    const flashAlpha = verdict === 'perfect' ? 0.55 : verdict === 'good' ? 0.3 : 0.55;
    const baseFlashLife = verdict === 'perfect' ? 0.32 : verdict === 'good' ? 0.18 : 0.26;
    const flashLife = isSuccessfulRelease ? baseFlashLife + 0.12 : baseFlashLife;
    if (verdict === 'miss') {
      for (let flashLane = 0; flashLane < MANIA_LANE_COUNT; flashLane += 1) {
        this.laneFlashSpecsThisFrame.push({
          lane: flashLane as 0 | 1 | 2 | 3,
          life: flashLife,
          alpha: flashAlpha,
          colour,
        });
      }
      this.shockwaveSpecsThisFrame.push({
        x,
        y,
        startR: 36,
        endR: 100,
        life: 0.3,
        thickness: 4,
        colour,
        alpha: 0.85,
      });
    } else {
      this.laneFlashSpecsThisFrame.push({
        lane: lane as 0 | 1 | 2 | 3,
        life: flashLife,
        alpha: isSuccessfulRelease ? flashAlpha + 0.1 : flashAlpha,
        colour,
      });
    }

    if (verdict !== 'miss') {
      const baseR = verdict === 'perfect' ? 28 : 22;
      const tapEndR = verdict === 'perfect' ? 110 : 80;
      const endR = isSuccessfulRelease ? tapEndR + 30 : tapEndR;
      this.shockwaveSpecsThisFrame.push({
        x,
        y,
        startR: baseR,
        endR,
        life: verdict === 'perfect' ? 0.45 : 0.32,
        thickness: 3,
        colour,
        alpha: 0.75,
      });
      if (verdict === 'perfect' && newCombo >= BIG_COMBO_BREAK_THRESHOLD) {
        this.shockwaveSpecsThisFrame.push({
          x,
          y,
          startR: 14,
          endR: 160,
          life: 0.6,
          thickness: 2,
          colour: MILESTONE_COLOUR,
          alpha: 0.5,
        });
      }
    }
  }

  private addShake(amount: number): void {
    if (this.reducedMotion) {
      return;
    }
    this.shake.amount = Math.max(this.shake.amount, amount);
    this.shake.decay = amount * 6;
  }

  private maybeAddPerfectShake(): void {
    const now = performance.now();
    if (now - this.lastPerfectShakeAt < 120) {
      return;
    }
    this.lastPerfectShakeAt = now;
    this.addShake(2.5);
  }

  /**
   * Gentle music-bus sidechain duck on every perfect — pumps the
   * music down ~1 dB so the hit click pops through the mix.
   * Throttled to {@link SIDECHAIN_DUCK_MIN_INTERVAL_MS} so jack
   * streams don't pump at frame rate (audible warble).
   */
  private applySidechainDuck(): void {
    const now = performance.now();
    if (now - this.lastSidechainDuckAt < SIDECHAIN_DUCK_MIN_INTERVAL_MS) {
      return;
    }
    this.lastSidechainDuckAt = now;
    const tune = MUSIC_DUCK_PERFECT_SIDECHAIN;
    this.audio.duckMusicPulse(tune.dip, tune.attackMs, tune.releaseMs);
  }

  /**
   * Push a fresh miss timestamp onto the rolling window and report
   * whether THIS specific miss should fire the multi-miss "crash"
   * cue. Returns true exactly once per crash episode: the first
   * miss that crosses {@link MULTI_MISS_THRESHOLD} inside an active
   * {@link MULTI_MISS_WINDOW_MS} window. Subsequent misses inside
   * the same episode return false so the crash boom + lowpass sweep
   * don't re-fire on every miss. The latch clears automatically
   * when the window empties (the player has stabilised for at least
   * `MULTI_MISS_WINDOW_MS`).
   */
  private registerMissAndDetectCrash(): boolean {
    const now = performance.now();
    const cutoff = now - MULTI_MISS_WINDOW_MS;
    while (this.recentMissTimestamps.length > 0 && this.recentMissTimestamps[0] < cutoff) {
      this.recentMissTimestamps.shift();
    }
    if (this.recentMissTimestamps.length === 0) {
      this.crashLatched = false;
    }
    this.recentMissTimestamps.push(now);
    if (this.recentMissTimestamps.length < MULTI_MISS_THRESHOLD) {
      return false;
    }
    if (this.crashLatched) {
      return false;
    }
    this.crashLatched = true;
    return true;
  }

  private maybeCelebrateComboMilestone(combo: number): void {
    for (const milestone of COMBO_MILESTONES) {
      if (combo >= milestone && !this.comboMilestonesHit.has(milestone)) {
        this.comboMilestonesHit.add(milestone);
        const tier = milestone >= 100 ? '#ff2bd6' : milestone >= 50 ? '#ffd84d' : milestone >= 25 ? '#3bff9d' : '#16e0ff';
        this.spawnBigBanner(`x${milestone} COMBO!`, tier);
        this.audio.scheduleComboMilestone();
        this.addShake(milestone >= 50 ? 8 : 5);
      }
    }
  }

  private spawnMilestoneFx(kind: MilestoneKind): void {
    const isStar = milestoneIsStar(kind);
    const tier = milestoneTier(kind);
    const colour = isStar ? '#ffd84d' : '#ffb13b';
    const centreX = this.cachedRect.width / 2;
    const centreY = this.cachedRect.height * 0.42;
    this.particleEmissionsThisFrame.push({
      type: 'milestone',
      x: centreX,
      y: centreY,
      colour,
    });
    this.particleEmissionsThisFrame.push({
      type: 'milestone',
      x: centreX - 40,
      y: centreY + 20,
      colour,
    });
    this.particleEmissionsThisFrame.push({
      type: 'milestone',
      x: centreX + 40,
      y: centreY + 20,
      colour,
    });
    const glyph = isStar ? '\u2605' : '\u{1F3C6}';
    this.spawnBigBanner(`${glyph} x${tier}`, colour);
    this.shockwaveSpecsThisFrame.push({
      x: centreX,
      y: centreY,
      startR: 28,
      endR: tier === 3 ? 280 : 210,
      life: tier === 3 ? 0.58 : 0.45,
      thickness: 3,
      colour,
      alpha: 0.72,
    });
    this.addShake(tier === 3 ? 14 : 6 + tier * 2);
    if (kind === 'star_3') {
      this.audio.duckMusic(0.12, 350);
      this.audio.playVictorySting();
    }
  }

  private handleEvent(ev: ManiaEngineEvent): void {
    if (ev.type === 'note_judged') {
      const res = ev.payload as ManiaResolution;
      this.spawnVerdictText(res);
      const guitarVerdict: 'perfect' | 'good' | 'miss' =
        res.verdict === 'perfect' ? 'perfect' : res.verdict === 'miss' ? 'miss' : 'good';
      const eventCombo = guitarVerdict === 'miss' ? 0 : res.previousCombo + 1;
      this.spawnHitFx(res.lane, guitarVerdict, eventCombo, res.kind);
      const pan = (res.lane / (MANIA_LANE_COUNT - 1)) * 1.4 - 0.7;
      // `laneEventsById` is populated from `engineConfig.sortedLaneEvents`
      // and rebuilt on every loop boundary; the engine guarantees every
      // resolution's `laneEventId` references one of those events, so
      // the lookup never returns undefined here.
      const laneEvent = this.laneEventsById.get(res.laneEventId)!;
      const isDualGem = laneEvent.kind === 'dual';
      const isHoldRelease = res.kind === 'release';
      if (guitarVerdict === 'perfect' || guitarVerdict === 'good') {
        this.spawnScorePop(res.lane, guitarVerdict, res.scoreDelta, eventCombo);
        this.audio.scheduleGuitarHitFeedback(guitarVerdict, undefined, {
          pan,
          comboNote: eventCombo,
          combo: eventCombo,
          isDual: isDualGem,
          isHoldRelease,
        });
        if (guitarVerdict === 'perfect') {
          this.applySidechainDuck();
          const stopMs =
            eventCombo >= BIG_COMBO_BREAK_THRESHOLD
              ? HIT_STOP_MS_PERFECT_BIG_COMBO
              : HIT_STOP_MS_PERFECT;
          this.renderer.requestHitStop(stopMs);
          this.maybeAddPerfectShake();
        } else {
          this.renderer.requestHitStop(HIT_STOP_MS_GOOD);
        }
        this.maybeCelebrateComboMilestone(eventCombo);
      } else {
        const isBigComboBreak = res.previousCombo >= BIG_COMBO_BREAK_THRESHOLD;
        const isCrash = this.registerMissAndDetectCrash();
        this.addShake(isCrash ? 16 : isBigComboBreak ? 12 : 7);
        if (isCrash) {
          // Multi-miss in a tight window — fire the heavier
          // stuttered "amp coughing" boom alongside the standard
          // miss click. The lowpass sweep below makes the music
          // dip its top end at the same instant so the player
          // hears the rig literally choking.
          this.audio.scheduleCrashBoom();
        } else if (isBigComboBreak) {
          this.audio.scheduleComboBreak();
        }
        this.audio.scheduleGuitarHitFeedback('miss', undefined, { pan });
        // Crash duck wins because the player is in a worse state than
        // a single big-combo break — multiple misses landed in quick
        // succession, regardless of how long the combo before it was.
        const duck = isCrash
          ? MUSIC_DUCK_CRASH
          : isBigComboBreak
            ? MUSIC_DUCK_COMBO_BREAK
            : MUSIC_DUCK_MISS;
        this.audio.duckMusicPulse(duck.dip, duck.attackMs, duck.releaseMs);
        if (isCrash) {
          const sweep = MUSIC_LOWPASS_CRASH;
          this.audio.sweepMusicLowpass(sweep.toFreq, sweep.attackMs, sweep.sustainMs, sweep.releaseMs);
        }
        this.comboMilestonesHit.clear();
      }
    } else if (ev.type === 'hp_changed') {
      const payload = ev.payload as ManiaHpChangedPayload;
      if (payload.reason === 'drained') {
        this.audio.playDeath();
        this.particleEmissionsThisFrame.push({
          type: 'death',
          x: this.cachedRect.width / 2,
          y: this.cachedRect.height * 0.5,
          colour: DEATH_COLOUR,
        });
        this.spawnBigBanner('GAME OVER', DEATH_COLOUR);
        this.addShake(14);
      } else if (payload.reason === 'lifeline') {
        this.spawnBigBanner('LIFELINE!', LIFELINE_COLOUR);
        this.addShake(10);
      } else if (payload.reason === 'empty_tap') {
        this.comboMilestonesHit.clear();
      }
    }
  }

  private finishSession(status: GuitarSessionStatus): void {
    this.setStatus(status);
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    const loopsCleared = this.getLoopsCleared();
    this.outcome = {
      completed: this.state.completed || loopsCleared >= 1,
      score: this.state.score,
      combo: this.state.combo,
      maxCombo: this.state.maxCombo,
      perfects: this.state.perfects,
      goods: this.state.goods,
      misses: this.state.misses,
      accuracy: summariseManiaAccuracy(this.state),
      deathBeat: this.state.deathBeat,
      deathReason: this.state.deathReason ?? undefined,
    };
    this.audio.stopMusic();
    if (this.state.completed && this.state.score > 0) {
      this.audio.playVictorySting();
    } else if (!this.state.completed && loopsCleared === 0) {
      this.audio.playFailureSting();
    }
    this.opts.onComplete?.(this.outcome);
  }

  private buildTestHook(): GuitarGameSessionTestHook {
    return {
      getOutcome: () => this.outcome,
      getStatus: () => this.status,
      getState: () => this.state,
      getBeat: () => this.audio.getCurrentBeat(),
      getEvents: () => this.allEvents.slice(),
      isAudioStarted: () => this.audio.isStarted,
    };
  }
}

export interface GuitarGameSessionTestHook {
  getOutcome: () => ReplayOutcome | null;
  getStatus: () => GuitarSessionStatus;
  getState: () => ManiaEngineState;
  getBeat: () => number;
  getEvents: () => ManiaEngineEvent[];
  isAudioStarted: () => boolean;
}

export { buildPerfectManiaReplayInputs } from '@shared/engine/mania/headlessRunner';
